import { AnimatePresence } from 'framer-motion';
import React, { FC, ReactNode, useCallback, useEffect, useState, memo } from 'react';
import { Crop } from 'react-image-crop';

import { ReactComponent as CropIcon } from '@/assets/icons/crop.svg';
import { ReactComponent as PreviewIcon } from '@/assets/icons/eye.svg';
import { ReactComponent as SettingsAppliedIcon } from '@/assets/icons/filter-applied.svg';
import { ReactComponent as SettingsIcon } from '@/assets/icons/filter.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import useDebounce from '@/hooks/useDebounce';

import CropModal from '../CropModal';
import PreviewModal from '../PreviewModal';
import {
  ActionButton,
  Buttons,
  DeleteButton,
  FullImg,
  ImageHolder,
  ImagesRow,
  Img,
  StyledTextarea,
} from './styledComponents';

interface BaseImageData {
  image: File;
  text?: string;
  croppedImage?: string;
  previousCrop?: Crop;
  quotes?: boolean;
  brakes?: boolean;
  textPosition?: string;
  divider?: string;
  actualPhoto?: string;
  hasMargins?: boolean;
  [key: string]: any;
}

interface Props<T extends BaseImageData = BaseImageData> {
  image: T;
  /** Index in images array */
  imageIndex: number;
  /** Update images callback */
  setImages: React.Dispatch<React.SetStateAction<T[]>>;
  /** Show preview button */
  showPreview?: boolean;
  /** Show settings button */
  showSettings?: boolean;
  /** Show text input */
  showText?: boolean;
  /** Use full-width image display */
  fullWidth?: boolean;
  /** Settings modal component */
  settingsModal?: ReactNode;
  /** Check if settings are applied (for icon state) */
  settingsApplied?: boolean;
  /** Debounce delay for text input (ms) */
  textDebounceDelay?: number;
  /** Placeholder for textarea */
  textPlaceholder?: string;
  /** Crop aspect ratio (width/height) for CropModal */
  aspectRatio?: number;
  /** Custom width for Img component */
  imgRatioX?: number;
  /** Custom height for Img component */
  imgRatioY?: number;
  /** Display image as circular (border-radius: 50%) */
  circularImage?: boolean;
  /** Let Crop ratio be not defined */
  freeFormCrop?: boolean;
}

/**
 * Generic Uploaded Image Row Component
 *
 * Provides common functionality for uploaded image rows:
 * - Image display with crop support
 * - Text input with debouncing
 * - Preview modal
 * - Settings modal (configurable)
 * - Crop modal
 * - Delete functionality
 *
 * @example
 * // Simple usage
 * <UploadedImageRow
 *   image={image}
 *   imageIndex={0}
 *   setImages={setImages}
 * />
 *
 * @example
 * // With preview and settings
 * <UploadedImageRow
 *   image={image}
 *   imageIndex={0}
 *   setImages={setImages}
 *   showPreview
 *   showSettings
 *   settingsModal={
 *     <SettingsModal
 *       image={image}
 *       imageIndex={0}
 *       setImages={setImages}
 *       onClose={() => {}}
 *       type="left"
 *     />
 *   }
 * />
 *
 * @example
 * // Full width with custom placeholder
 * <UploadedImageRow
 *   image={image}
 *   imageIndex={0}
 *   setImages={setImages}
 *   fullWidth
 *   textPlaceholder="Enter custom text"
 * />
 */
const UploadedImageRow: FC<Props> = ({
  image,
  imageIndex,
  setImages,
  showPreview = false,
  showSettings = false,
  showText = true,
  fullWidth = false,
  settingsModal,
  settingsApplied = false,
  textDebounceDelay = 300,
  textPlaceholder = 'Add Text',
  aspectRatio,
  imgRatioX,
  imgRatioY,
  circularImage = false,
  freeFormCrop = false,
}) => {
  const [text, setText] = useState(image.text || '');
  const [showModal, setShowModal] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [croppedImage, setCroppedImage] = useState(image.croppedImage);
  const [imageUrl, setImageUrl] = useState(() =>
    URL.createObjectURL(image.image),
  );

  const debouncedText = useDebounce(text, textDebounceDelay);

  // Update image URL when image changes and cleanup old URL
  useEffect(() => {
    const newUrl = URL.createObjectURL(image.image);
    setImageUrl(newUrl);

    return () => {
      URL.revokeObjectURL(newUrl);
    };
  }, [image.image]);

  // Sync croppedImage state with image prop
  useEffect(() => {
    setCroppedImage(image.croppedImage);
  }, [image.croppedImage]);

  const removeImage = () =>
    setImages((prev) => prev.filter((_, idx) => idx !== imageIndex));

  useEffect(() => {
    if (!showText) return;

    setImages((prevImages) => {
      const newImages = [...prevImages];
      newImages[imageIndex] = {
        ...image,
        text: debouncedText,
      };
      return newImages;
    });
    // eslint-disable-next-line
  }, [debouncedText, showText]);

  const onTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  const handleCroppedImage = useCallback(
    async (base64: string) => {
      try {
        setImages((prev) => {
          const newArray = [...prev];
          newArray[imageIndex] = {
            ...prev[imageIndex],
            croppedImage: base64,
          };
          return newArray;
        });
        setCroppedImage(base64);
      } catch (error) {}
    },
    [setImages, imageIndex],
  );

  const handleSetPreviousCrop = useCallback(
    (crop: Crop | undefined) => {
      setImages((prev) => {
        const newArray = [...prev];
        newArray[imageIndex] = {
          ...prev[imageIndex],
          previousCrop: crop,
        };
        return newArray;
      });
    },
    [setImages, imageIndex],
  );

  // const ImageComponent = fullWidth ? FullImg : Img;
  const renderImageComponent = () => {
    if (fullWidth) {
      return (
        <FullImg
          src={!!croppedImage ? croppedImage : imageUrl}
          alt="uploaded image"
        />
      );
    }
    if (imgRatioX && imgRatioY) {
      return (
        <Img
          $width={imgRatioX}
          $height={imgRatioY}
          $circular={circularImage}
          src={!!croppedImage ? croppedImage : imageUrl}
          alt="uploaded image"
        />
      );
    }
    return (
      <Img
        $circular={circularImage}
        src={!!croppedImage ? croppedImage : imageUrl}
        alt="uploaded image"
      />
    );
  };

  return (
    <>
      <ImagesRow>
        <ImageHolder>{renderImageComponent()}</ImageHolder>
        {showText && (
          <StyledTextarea
            value={text}
            placeholder={textPlaceholder}
            onChange={onTextAreaChange}
          />
        )}
        <Buttons>
          {showPreview && (
            <ActionButton onClick={() => setShowPreviewModal(true)}>
              <PreviewIcon />
            </ActionButton>
          )}
          {showSettings && (
            <ActionButton onClick={() => setShowModal(true)}>
              {settingsApplied ? <SettingsAppliedIcon /> : <SettingsIcon />}
            </ActionButton>
          )}
          <ActionButton onClick={() => setShowCropModal(true)}>
            <CropIcon />
          </ActionButton>
          <DeleteButton onClick={removeImage}>
            <TrashIcon />
          </DeleteButton>
        </Buttons>
      </ImagesRow>
      <AnimatePresence>
        {!!showModal &&
          settingsModal &&
          React.cloneElement(settingsModal as React.ReactElement, {
            onClose: () => setShowModal(false),
          })}
        {!!showCropModal && (
          <CropModal
            freeFormCrop={freeFormCrop}
            aspectRatio={aspectRatio}
            imageSrc={imageUrl}
            onClose={() => setShowCropModal(false)}
            onCrop={handleCroppedImage}
            previousCrop={image.previousCrop}
            setPreviousCrop={handleSetPreviousCrop}
          />
        )}
        {!!showPreviewModal && (
          <PreviewModal
            image={image}
            onClose={() => setShowPreviewModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default memo(UploadedImageRow);
